import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { supabase } from './supabase';
import { generateNonce, sha256Hex } from './platform';
import * as Sentry from './errorReporting';

export const NATIVE_REDIRECT_URI = 'sliptrack://auth/callback';

// #1503 P1-6 (login CSRF): a callback finishes only a Google sign-in this JS context started,
// only by a PKCE code exchange (the verifier lives in auth-js storage), and never from tokens.
const MAX_AUTH_CODE_LENGTH = 512;
let pendingGoogleSignIn = false;
let googleSignInInFlight = null;
const authCodeExchanges = new Map(); // code -> promise; cold start delivers the same URL twice

// Single job: true only for our exact callback URL (no look-alike paths, schemes or non-strings).
function isAuthCallbackUrl(url) {
  if (typeof url !== 'string' || !url.startsWith(NATIVE_REDIRECT_URI)) return false;
  const next = url.charAt(NATIVE_REDIRECT_URI.length);
  return next === '' || next === '?' || next === '#';
}

// Single job: true for a 1-512 character code with no whitespace; the server checks the rest.
function isValidAuthCode(code) {
  return typeof code === 'string'
    && code.length >= 1
    && code.length <= MAX_AUTH_CODE_LENGTH
    && !/\s/.test(code);
}

// Recoverable by design (the user can tap sign-in again): a warning (CC-IMPL-037).
// Only the error's name leaves the device -- never the URL, the code or a token.
function reportAuthCallback(failureStage, err) {
  Sentry.captureMessage(failureStage, {
    level: 'warning',
    tags: { function: 'handleAuthCallback', failureStage },
    extra: { errorName: err?.name },
  });
}

// An exception nobody planned for is terminal: a code bug here fails every Google sign-in, and a
// retry hits it again (CC-IMPL-037). Expected failures above stay warnings.
function reportUnexpected(err) {
  Sentry.captureException(err, {
    tags: { function: 'handleAuthCallback', failureStage: 'auth-callback-unexpected' },
    extra: { errorName: err?.name },
  });
}

// auth-js treats any of these as an OAuth error redirect; so do we (a cancel, never an exchange).
const AUTH_ERROR_KEYS = ['error', 'error_code', 'error_description'];

export function isNative() {
  return Capacitor.isNativePlatform();
}

// iOS-only predicate — stricter than isNative() (which is also true on Android).
// Apple sign-in is iOS-only in V1; Android and web must never reach the plugin (#1002).
export const isIOS = () => Capacitor.getPlatform() === 'ios';

// Apple cancellation classifier. In V1 this runs ONLY on the Apple path (the Google/web path
// keeps its own inline classifier in AuthScreen), so it classifies Apple cancels only — it does
// NOT apply the Google web tokens (cancel/dismiss/popup_closed), because an Apple-path network
// abort whose message contains "cancelled" is a GENUINE failure, not a user cancel.
// Apple rejects with error.localizedDescription; ASAuthorizationError.canceled = code 1001.
// Require BOTH the domain fragment AND the exact, word-bounded code so other AuthorizationError
// codes (1000/1002/1003/1004) and a stray "1001" inside a request id are genuine failures.
export const isAuthCancellation = (err) => {
  const m = (err?.message || '').toLowerCase();
  return m.includes('authorizationerror') && /\b1001\b/.test(m);
};

// Native Sign in with Apple: native sheet -> identityToken -> Supabase signInWithIdToken.
// Nonce is load-bearing: Apple signs the HASHED nonce; Supabase verifies the RAW nonce.
export async function signInWithAppleNative() {
  const rawNonce = generateNonce();
  const hashedNonce = await sha256Hex(rawNonce);
  const { response } = await SignInWithApple.authorize({
    scopes: 'email name',   // space-delimited STRING per plugin 7.1.0 contract
    nonce: hashedNonce,     // Apple signs the HASHED nonce
    // clientId/redirectURI omitted — required by the TS type but native-ignored (web-only, deferred)
  });
  const idToken = response?.identityToken;
  if (!idToken) throw new Error('apple-signin: missing identityToken');
  const { error } = await supabase.auth.signInWithIdToken({
    provider: 'apple',
    token: idToken,
    nonce: rawNonce,        // Supabase verifies against the RAW nonce
  });
  if (error) throw error;
}

// Re-run the native Apple sheet ONLY to obtain a fresh authorizationCode (account deletion,
// #1079): Apple's revoke API needs a token and no refresh token is stored at sign-in. Does
// NOT call Supabase -- the user is already signed in. The nonce only satisfies Apple's request
// shape here; nobody verifies it (no signInWithIdToken), unlike the load-bearing sign-in nonce.
export async function reauthorizeWithAppleNative() {
  const rawNonce = generateNonce();
  const hashedNonce = await sha256Hex(rawNonce);
  const { response } = await SignInWithApple.authorize({
    scopes: 'email name',
    nonce: hashedNonce,
  });
  const authorizationCode = response?.authorizationCode;
  if (!authorizationCode) throw new Error('apple-reauth: missing authorizationCode');
  return { authorizationCode };
}

// Single-flight (#1503 P1-6): a second tap while the first is starting would overwrite the PKCE
// verifier and make the second Browser.open reject. The pending marker is set just before the
// browser opens; handleAuthCallback claims it.
export function signInWithGoogleNative() {
  if (googleSignInInFlight) return googleSignInInFlight;
  googleSignInInFlight = (async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: NATIVE_REDIRECT_URI,
        queryParams: { access_type: 'offline', prompt: 'select_account' },
        skipBrowserRedirect: true,
      },
    });
    if (error) throw error;
    if (data?.url) {
      pendingGoogleSignIn = true;
      try {
        await Browser.open({ url: data.url });
      } catch (openErr) {
        pendingGoogleSignIn = false; // no browser, so no callback can belong to this attempt
        throw openErr; // useAuth.signInWithGoogle reports it
      }
    }
  })().finally(() => { googleSignInInFlight = null; });
  return googleSignInInFlight;
}

// Single job: turn one sliptrack://auth/callback URL into at most one PKCE code exchange, and only
// for a Google sign-in this JS context started. Never reads tokens; never rejects.
export function handleAuthCallback(url) {
  if (!isAuthCallbackUrl(url)) return Promise.resolve();
  try {
    const rest = url.slice(NATIVE_REDIRECT_URI.length);
    const hashAt = rest.indexOf('#');
    const queryPart = hashAt === -1 ? rest : rest.slice(0, hashAt);
    const query = new URLSearchParams(queryPart.startsWith('?') ? queryPart.slice(1) : '');
    const fragment = new URLSearchParams(hashAt === -1 ? '' : rest.slice(hashAt + 1));
    if (['access_token', 'refresh_token'].some((k) => query.has(k) || fragment.has(k))) {
      // The browser and the pending marker are left alone: a real sign-in may still be open.
      reportAuthCallback('auth-callback-token-rejected');
      return Promise.resolve();
    }
    const code = query.get('code');
    if (code && authCodeExchanges.has(code)) return authCodeExchanges.get(code);
    if (!pendingGoogleSignIn) return Promise.resolve();
    pendingGoogleSignIn = false; // claimed synchronously, before any await
    const run = finishGoogleSignIn(query, fragment, code);
    if (code) authCodeExchanges.set(code, run);
    return run;
  } catch (err) {
    reportUnexpected(err);
    return Promise.resolve();
  }
}

// Single job: finish one claimed callback (cancel, code check, exchange, browser close); never rejects.
async function finishGoogleSignIn(query, fragment, code) {
  try {
    if (AUTH_ERROR_KEYS.some((k) => query.has(k) || fragment.has(k))) return; // cancelled at Google
    if (!isValidAuthCode(code)) return;
    const { error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) reportAuthCallback('auth-callback-exchange-failed', error);
  } catch (err) {
    reportUnexpected(err);
  } finally {
    // This JS context opened the browser for this sign-in, so a close failure is a real anomaly.
    try {
      await Browser.close();
    } catch (closeErr) {
      reportAuthCallback('auth-callback-browser-close-failed', closeErr);
    }
  }
}

export async function setupNativeAuthListener() {
  const handle = await App.addListener('appUrlOpen', ({ url }) => handleAuthCallback(url));

  // Cold-start deep link, awaited so callers can wait for session hydration before querying
  // getSession(). If the retained appUrlOpen already claimed this code, handleAuthCallback
  // returns that same in-flight promise, so this still waits for the exchange.
  try {
    const { url } = (await App.getLaunchUrl()) || {};
    if (url) await handleAuthCallback(url);
  } catch (err) {
    // handleAuthCallback never rejects, so this is getLaunchUrl itself. Sign-in still works.
    Sentry.captureMessage('auth-launch-url-failed', {
      level: 'warning',
      tags: { function: 'setupNativeAuthListener', failureStage: 'auth-launch-url-failed' },
      extra: { errorName: err?.name },
    });
  }

  return () => handle.remove();
}
